/**
 * Handshake client — exchanges a `widgetId` (+ optional `visitorId`) for a
 * `sessionToken` and the widget's runtime config (branding, pre-chat form).
 *
 * This is the SAME endpoint used by curl smoke tests during backend dev:
 *   POST /api/v1/webchat/handshake
 *   Headers: Origin: <host-page-origin>  (browser sets it automatically)
 *   Body:    { widgetId, visitorId?, proofOfWork? }
 *
 * The `Origin` header is set by the browser when running in a real page — we
 * MUST NOT set it manually (browsers block that).
 */

import type {
  HandshakeRequest,
  HandshakeResponse,
  ProofOfWorkSolution,
} from './api-types';

export class HandshakeError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
  ) {
    super(`Handshake failed (HTTP ${status}): ${code}`);
    this.name = 'HandshakeError';
  }
}

export async function handshake(
  apiBaseUrl: string,
  widgetId: string,
  visitorId?: string,
  proofOfWork?: ProofOfWorkSolution,
  attribution?: Record<string, unknown>,
): Promise<HandshakeResponse> {
  const body: HandshakeRequest = { widgetId };
  if (visitorId) body.visitorId = visitorId;
  if (proofOfWork) body.proofOfWork = proofOfWork;
  if (attribution) body.attribution = attribution;

  let response: Response;
  try {
    response = await fetch(`${apiBaseUrl}/api/v1/webchat/handshake`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch (err) {
    // Network-level failure (CORS, DNS, offline). No response body to parse.
    throw new HandshakeError(0, (err as Error).message || 'network_error');
  }

  if (!response.ok) {
    let code = 'handshake_failed';
    try {
      const errBody = (await response.json()) as { message?: string };
      if (typeof errBody.message === 'string') code = errBody.message;
    } catch {
      // body wasn't JSON
    }
    throw new HandshakeError(response.status, code);
  }

  return (await response.json()) as HandshakeResponse;
}
